import clsx from 'clsx';
import { camelCase, isEmpty } from 'lodash';
import { bool, string } from 'prop-types';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';

import {
  ArticleSkeleton,
  Banner,
  BookingBloc,
  Breadcrumb,
  HeroBanner,
  Icon,
  SanitizedHtml,
  SocialShare,
  StrapiImg
} from 'components';
import { useFetch } from 'hooks';
import { routes } from 'router';
import { appService } from 'services';

const ArticlePage = ({ type, isItemPage = false, filterByDate = false, listCrumb = null }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { id } = useParams();
  const { call: articleCall, loading: articleLoading, data: articleData } = useFetch({ filterUserCompany: true });
  const { call: itemShowCall, loading: itemShowLoading, data: itemShow } = useFetch({ filterUserCompany: true });

  const article = articleData?.data?.[0] || itemShow?.data;
  const loading = articleLoading || itemShowLoading;
  const error = articleData?.error || itemShow?.error;

  const breadcrumbs = useMemo(() => {
    let crumbs = [{ label: t('breadcrumb.home'), url: routes.home.path }, { label: article?.heroTitle || '' }];

    if (isItemPage || listCrumb) {
      crumbs.splice(1, 0, { label: t(`breadcrumb.${camelCase(type)}`), url: routes[listCrumb].path });
    }

    return crumbs;
  }, [article]);

  useEffect(() => {
    isItemPage ? getItemShow() : getArticle();
  }, [type, isItemPage, location.pathname]);

  const getArticle = () => {
    articleCall({
      service: appService.getStrapiServiceName(),
      method: 'get',
      path: '/api/articles',
      query: {
        filters: {
          page_types: { name: { $eq: type } }
        }
      }
    });
  };

  const getItemShow = () => {
    const today = new Date();

    const filters = {
      ...(filterByDate && {
        end_date: {
          $gt: today
        },
        start_date: {
          $lt: today
        }
      })
    };

    itemShowCall(
      {
        service: appService.getStrapiServiceName(),
        method: 'get',
        path: `/api/${type}/${id}`,
        query: {
          filters,
          populate: 'deep,5'
        }
      },
      { hideToast: true }
    );
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <>
      {!isEmpty(article) && (
        <div className="hidden lg:block">
          <HeroBanner
            title={article?.heroTitle}
            image={article?.heroImageDesktop}
            mobileImage={article?.heroImageMobile}
            loading={loading}
          />
        </div>
      )}

      {article?.pageImage && <StrapiImg image={article?.pageImage} className="mt-20 lg:hidden w-full" />}

      <div
        className={clsx(
          'main-container rounded-t-3xl relative -top-8 bg-white pt-8 print:rounded-none print:top-0',
          article?.pageImage ? 'lg:mt-20' : 'mt-20'
        )}
      >
        {loading && <ArticleSkeleton />}

        {!loading && !isEmpty(article) && (
          <>
            <div className="hidden lg:block mb-4 print:block">
              <Breadcrumb breadcrumbItems={breadcrumbs} />
            </div>

            <div className="hidden lg:flex justify-between items-center mb-14">
              <h2 className="h1 underlined-blue hidden lg:inline">{article?.pageTitle}</h2>

              <div className="flex items-center gap-x-6 print:hidden">
                <SocialShare />
                <button type="button" onClick={handlePrint} className="text-gray-500">
                  <Icon name="print" iconStyle="fad" classes="h-5 w-5" />
                </button>
              </div>
            </div>

            {article?.pageImage && (
              <div className="rounded-2xl my-12 overflow-hidden hidden lg:block">
                <StrapiImg
                  image={article?.pageImage}
                  className="w-full h-full max-h-[32rem] object-cover object-center"
                />
              </div>
            )}

            <div className="flex lg:gap-x-16">
              <div className="flex flex-col gap-6">
                <h2 className="h1 lg:hidden">{article?.heroTitle}</h2>

                {article?.paragraphs?.map((paragraph, index) => (
                  <div key={`paragraph-${index}`} className="flex flex-col gap-4">
                    <h3 className="h3 underlined-blue">{paragraph?.title}</h3>
                    <SanitizedHtml content={paragraph?.content} className="text-gray-600" />
                  </div>
                ))}
              </div>

              {!isEmpty(article?.reservationBloc) && (
                <div className="reservation-bloc-container">
                  <BookingBloc bookingBlocLoading={loading} blocks={article?.reservationBloc} />
                </div>
              )}
            </div>
          </>
        )}

        {/* TODO: text to change when available */}
        {!loading && isEmpty(article) && error && (
          <>
            <div className="hidden lg:block mt-20 mb-4">
              <Breadcrumb breadcrumbItems={breadcrumbs} />
            </div>
            <Banner icon={{ name: 'circle-info', style: 'fad' }} type="warning" className="my-12">
              {t('contentUnavailable')}
            </Banner>
          </>
        )}
      </div>
    </>
  );
};

ArticlePage.propTypes = {
  type: string.isRequired,
  isItemPage: bool,
  filterByDate: bool,
  listCrumb: string
};

export default ArticlePage;
